import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listBranches } from '../../api/branches';

interface BulkTransferDialogProps {
  open: boolean;
  leadCount: number;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (toBranchId: number, reason?: string) => Promise<void>;
}

export const BulkTransferDialog = ({ open, leadCount, errorMessage, onClose, onSubmit }: BulkTransferDialogProps) => {
  const [toBranchId, setToBranchId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const branchesQuery = useQuery({
    queryKey: ['branches-for-transfer'],
    queryFn: () => listBranches(true),
    enabled: open,
  });

  const handleSubmit = async () => {
    if (!toBranchId) return;
    setSubmitting(true);
    try {
      await onSubmit(toBranchId, reason || undefined);
      setReason('');
      setToBranchId('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Transfer {leadCount} lead(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Choose a branch to transfer the selected leads to.
          </Typography>
          <TextField
            select
            label="Transfer to branch"
            fullWidth
            value={toBranchId}
            onChange={(e) => setToBranchId(Number(e.target.value))}
          >
            {branchesQuery.data?.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason (optional)"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!toBranchId || submitting} onClick={handleSubmit}>
          {submitting ? 'Transferring…' : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
